import React from 'react';
import { useIntl } from "react-intl";
import {
    Bell,
    Briefcase,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    Check,
    File,
    Files,
    Layers,
    Plus,
    Tag,
    Trash,
    User
} from 'lucide-react';
import { ReactComponent as StatusIcon } from "../../svg/status.svg";
import { getContrastColors } from "../../UIElements";
import { getUserInfo } from "../../DataFunctions";
import { renderIcon } from "../../utils/botIconUtils";
import { getTopicDisplayName, getThemeColor } from "../../utils/topicUtils";
import './BotCard.scss';

export { renderIcon };

export const BotCard = ({
    // Data
    data,
    botId,
    userDataSource = null,
    topicDataSource = null,

    // Display mode
    mode = 'view', // 'view' | 'select'

    // State
    isExpanded,
    isSelected = false,

    // Callbacks
    onHeaderClick,
    onToggleExpanded,
    onVersionChange,
    onToggleSelection,
    onCreateVersion,
    onDelete,

    // AI provider
    isGemini = false,

    // Version handling
    availableVersions = [],
    showVersionBadge = mode === 'view',
    showVersionButtons = mode === 'view',
    showDeleteIcon = mode === 'view',
    showStatusBadge = mode === 'view',
    showToggleSection = mode === 'view',
    showDescriptionInHeader = mode === 'view',
    showDescriptionInContent = mode === 'select',
    showVersionDropdown = mode === 'select',
}) => {
    const intl = useIntl();
    const status = data?.status || null;
    const config = data.config || {};
    const {
        name,
        description,
        coordinator,
        supportTopics,
        supportRequestTarget,
        materials,
        corpora,
        color,
        icon,
        version,
        versions = [],
        versionGroup
    } = config;

    const corporaKeys = Object.keys(corpora || {});

    const { textColor, iconBackground } = getContrastColors(color || '#9EA5F8');
    const hasVersionOptions = availableVersions && availableVersions.length > 1;

    const handleToggle = () => {
        onToggleExpanded?.(botId, versionGroup, !isExpanded);
    };

    const handleHeaderClick = () => {
        if (mode === 'view' && onHeaderClick) {
            onHeaderClick(botId);
        } else if (mode === 'select') {
            handleToggle();
        }
    };

    const handleVersionDropdownChange = (e) => {
        const newVersion = e.target.value;
        if (versionGroup) {
            onVersionChange?.(versionGroup, newVersion, botId);
        }
    };

    const handleVersionButtonClick = (newVersion) => {
        if (versionGroup) {
            onVersionChange?.(versionGroup, newVersion);
        }
    };

    const handleCreateNewVersion = (e) => {
        e.stopPropagation();
        onCreateVersion?.(data);
    };

    const coordInfo = getUserInfo(userDataSource, coordinator);
    const coordInitials = coordInfo?.initials;
    const coordName = coordInfo?.name;
    const coordRole = coordInfo?.role ? `, ${coordInfo.role}` : "";
    const coordEmail = coordInfo?.email || "";

    return <div
        className={`support-bots__bot ${isExpanded ? "open" : "closed"} ${isSelected ? "selected" : "unselected"}`}
    >
        <div
            className={"support-bots__bot-header"}
            onClick={handleHeaderClick}
            style={{
                backgroundColor: color || '#9EA5F8',
                color: textColor,
                opacity: mode === 'select' && isSelected ? 1 : mode === 'select' ? 0.8 : 1
            }}
        >
            {mode === 'select' && (
                <div className="topic-toggle">
                    <ChevronRight
                        size={16}
                        style={{
                            transform: !isExpanded ? 'rotate(0deg)' : 'rotate(90deg)',
                        }}
                    />
                </div>
            )}

            <div className={"support-bots__bot-header-icon"} style={{
                backgroundColor: iconBackground
            }}>
                {renderIcon(icon)}
            </div>

            <div className={"support-bots__bot-header-texts"}>
                {mode === 'view' ? (
                    <>
                        <h2>
                            {name}
                            {showVersionBadge && version !== "DEFAULT" && (
                                <span className={"version"}>[{version}]</span>
                            )}
                        </h2>
                        {showStatusBadge && status === "DRAFT" && (
                            <div className={"bot-status draft"}>
                                <StatusIcon />
                                {intl.formatMessage({
                                    defaultMessage: "Draft",
                                    id: `bot.status.draft`,
                                })}
                            </div>
                        )}
                        {showDescriptionInHeader && (
                            <p>{description}</p>
                        )}
                    </>
                ) : (
                    <label>
                        {name}
                        {showVersionDropdown && hasVersionOptions && (
                            <select
                                className="version-select"
                                value={version}
                                onClick={e => e.stopPropagation()}
                                onChange={handleVersionDropdownChange}
                            >
                                {availableVersions.map(v => (
                                    <option key={v} value={v}>
                                        {v === "DEFAULT" ?
                                            intl.formatMessage({
                                                defaultMessage: "Default",
                                                id: "default"
                                            }) : v
                                        }
                                    </option>
                                ))}
                            </select>
                        )}
                    </label>
                )}
            </div>

            {showDeleteIcon && (
                <div
                    className={"support-bots__bot-header-delete"}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(botId, versionGroup, version);
                    }}
                    title={intl.formatMessage({ id: 'survey.delete', defaultMessage: 'Delete' })}
                >
                    <Trash size={16} />
                </div>
            )}

            {mode === 'select' && (
                <button
                    className="support-bots__bot-select-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelection?.(botId);
                    }}
                    title={isSelected ?
                        intl.formatMessage({
                            id: 'bot.deselect',
                            defaultMessage: 'Remove this bot'
                        }) :
                        intl.formatMessage({
                            id: 'bot.select',
                            defaultMessage: 'Add this bot'
                        })
                    }
                >
                    {isSelected ? (
                        <>
                            <Check size={18} />
                            <span>
                                        {intl.formatMessage({
                                            id: 'bot.selected',
                                            defaultMessage: 'Selected'
                                        })}
                                    </span>
                        </>
                    ) : (
                        <>
                            <Plus size={18} />
                            <span>
                                        {intl.formatMessage({
                                            id: 'bot.select',
                                            defaultMessage: 'Select'
                                        })}
                                    </span>
                        </>
                    )}
                </button>
            )}
        </div>

        {showToggleSection && (
            <div className={"support-bots__bot-toggle"} onClick={handleToggle}>
                <div className={"support-bots__bot-toggle-icons"}>
                    <div
                        className={"support-bots__bot-toggle-icons-topics"}
                        title={intl.formatMessage({
                            defaultMessage: 'Support Topics',
                            id: 'supportTopics'
                        })}
                    >
                        <Tag size={16} /> {supportTopics?.length}
                    </div>
                    <div
                        className={"support-bots__bot-toggle-icons-materials"}
                        title={intl.formatMessage(isGemini
                            ? { defaultMessage: 'Knowledge Bases', id: 'knowledgeBases' }
                            : { defaultMessage: 'Reference Materials', id: 'referenceMaterials' }
                        )}
                    >
                        {isGemini
                            ? <><Files size={16} /> {corporaKeys.length}</>
                            : <><File size={16} /> {(materials || [])?.length}</>
                        }
                    </div>
                    <div
                        className={"support-bots__bot-toggle-icons-versions"}
                        title={intl.formatMessage({
                            defaultMessage: 'Versions',
                            id: 'versions'
                        })}
                    >
                        <Layers size={16} /> {(versions || [])?.length}
                    </div>
                </div>
                <div className={"support-bots__bot-toggle-chevron"}>
                    {isExpanded ?
                        <>
                            {intl.formatMessage({
                                defaultMessage: 'Show less',
                                id: 'showLess'
                            })}
                            <ChevronUp />
                        </> :
                        <>
                            {intl.formatMessage({
                                defaultMessage: 'Show more',
                                id: 'showMore'
                            })}
                            <ChevronDown />
                        </>
                    }
                </div>
            </div>
        )}

        {isExpanded && (
            <div className={"support-bots__bot-content"}>
                {showDescriptionInContent && (
                    <div className={"support-bots__bot-content-group"}>
                        <p>{description}</p>
                    </div>
                )}

                {showVersionButtons && versions.length > 0 && (
                    <div className={"support-bots__bot-content-group"}>
                        <h4>
                            <Layers />
                            {intl.formatMessage({
                                defaultMessage: 'Versions',
                                id: 'versions'
                            })}
                        </h4>
                        <div className={"support-bots__bot-content-group-versions"}>
                            {versions.map((v, i) => {
                                const isActive = v === version;
                                return (
                                    <button
                                        key={i}
                                        className={`support-bots__bot-content-group-versions-button ${isActive ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isActive) {
                                                handleVersionButtonClick(v);
                                            }
                                        }}
                                    >
                                        {v === "DEFAULT" ?
                                            intl.formatMessage({
                                                defaultMessage: "Default",
                                                id: "default"
                                            }) : v
                                        }
                                    </button>
                                );
                            })}
                            {onCreateVersion && (
                                <button
                                    className="support-bots__bot-content-group-versions-create"
                                    onClick={handleCreateNewVersion}
                                >
                                    <Plus size={14} />
                                    {intl.formatMessage({
                                        defaultMessage: 'New version',
                                        id: 'bot.version.createNew'
                                    })}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className={"support-bots__bot-content-group"}>
                    <h4>
                        <Tag />
                        {intl.formatMessage({
                            defaultMessage: 'Support Topics',
                            id: 'supportTopics'
                        })}
                    </h4>
                    <div className={"support-bots__bot-content-group-topics"}>
                        {!supportTopics?.length ?
                            intl.formatMessage({
                                defaultMessage: 'No support topics assigned to the bot.',
                                id: 'noTopicsAssigned'
                            }) :
                            supportTopics.map((topic, i) => {
                                const themeColor = getThemeColor(topicDataSource?.[topic]?.theme);
                                return (
                                    <div
                                        key={i}
                                        className={"support-bots__bot-content-group-topics-topic"}
                                        style={{backgroundColor: themeColor, color: getContrastColors(themeColor).textColor}}
                                        title={data?.config?.topicSettings?.[topic]?.instructions || topicDataSource?.[topic]?.instructions || ""}
                                    >
                                        {getTopicDisplayName(topic, topicDataSource?.[topic], intl)}
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>

                <div className={"support-bots__bot-content-group"}>
                    <h4>
                        <Bell />
                        {intl.formatMessage({
                            defaultMessage: 'Support Request Target',
                            id: 'supportRequestTarget'
                        })}
                    </h4>
                    <div className={"support-bots__bot-content-group-requests"}>
                        {!supportRequestTarget?.length ?
                            intl.formatMessage({
                                defaultMessage: 'This bot will not create support requests.',
                                id: 'noRequestsAssigned'
                            }) :
                            <div className={"support-bots__bot-content-group-requests-request"}>
                                <Briefcase size={14} />
                                {intl.formatMessage({
                                    defaultMessage: supportRequestTarget,
                                    id: `role.${supportRequestTarget}`
                                })}
                            </div>
                        }
                    </div>
                </div>

                <div className={"support-bots__bot-content-group"}>
                    <h4>
                        {isGemini ? <Files /> : <File />}
                        {intl.formatMessage(isGemini
                            ? { defaultMessage: 'Knowledge Bases', id: 'knowledgeBases' }
                            : { defaultMessage: 'Reference Materials', id: 'referenceMaterials' }
                        )}
                    </h4>
                    <div className={"support-bots__bot-content-group-materials"}>
                        {isGemini ? (
                            corporaKeys.length === 0 ?
                                intl.formatMessage({
                                    defaultMessage: 'No knowledge bases linked.',
                                    id: 'noKnowledgeBases'
                                }) :
                                corporaKeys.map((slug) => (
                                    <div key={slug} className={"support-bots__bot-content-group-materials-material"}>
                                        <Files size={14} />
                                        <div className={"support-bots__bot-content-group-materials-material-name"}>
                                            {slug}
                                        </div>
                                    </div>
                                ))
                        ) : (
                            !(materials && materials?.length) ?
                                intl.formatMessage({
                                    defaultMessage: 'This bot will not use reference materials for responses.',
                                    id: 'noMaterialsAssigned'
                                }) :
                                materials.map((material, i) => (
                                    <div key={i} className={"support-bots__bot-content-group-materials-material"}>
                                        <File size={14} />
                                        <div className={"support-bots__bot-content-group-materials-material-name"}>
                                            {typeof material === 'object' ? material.filename : material}
                                        </div>
                                    </div>
                                ))
                        )}
                    </div>
                </div>

                <div className={"support-bots__bot-content-group"}>
                    <h4>
                        <User />
                        {intl.formatMessage({
                            defaultMessage: 'Bot Coordinator',
                            id: 'botCoordinator'
                        })}
                    </h4>
                    <div className={"support-bots__bot-content-group-coordinator"}>
                        <div className={"support-bots__bot-content-group-coordinator-card"}>
                            <div className={"support-bots__bot-content-group-coordinator-card-initials profile-icon"}>
                                {coordInitials}
                            </div>
                            <div className={"support-bots__bot-content-group-coordinator-card-text"}>
                                <div className={"support-bots__bot-content-group-coordinator-card-text-name"}>
                                    {coordName}{coordRole}
                                </div>
                                <div className={"support-bots__bot-content-group-coordinator-card-text-id"}>
                                    {coordEmail}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}
    </div>
};
